'use client';

import { useRef, useMemo, useContext, useState, useEffect } from 'react';
import LinkNoPrefetch from '@/components/link-noprefetch';

import { IconChevronDown } from '@tabler/icons-react';
import { ActiveSectionContext } from './LinkSection';
import { decode } from 'html-entities';
/* Utils */
const getTextLink = (node) => {
    const { text, marks } = node;
    const mark = marks?.find((mark) => mark.type === 'link');
    const link = mark?.attrs.href || '/';
    return { text, link };
};

/**
 * LinkHeadingBlock component
 * @description This component is responsible for rendering the heading block in the footer links section
 * @param {*} { content = [] }
 * @return {*}
 */
export const LinkHeadingBlock = (node) => {
    const {
        node: { content = [] }
    } = node;
    const ref = useRef(null);
    const { activeSection, setActiveSection } = useContext(ActiveSectionContext);
    const text = content?.map((block) => block?.text)?.join(' ');
    // Create a section id based on the heading text
    const sectionId = `section-${text}`;

    const handleClick = () => {
        setActiveSection((previewSection) => {
            if (previewSection === sectionId) {
                return '';
            }

            return sectionId;
        });
    };

    return (
        <h3
            id={sectionId}
            ref={ref}
            onClick={handleClick}
            className="lg:text-lg flex cursor-pointer select-none items-center justify-between text-base font-semibold md:pointer-events-none md:cursor-auto md:select-auto md:border-b md:border-b-fuschia md:pb-6 md:text-xl">
            {decode(text)}
            <IconChevronDown
                className={`block size-5 md:hidden ${
                    activeSection === sectionId ? 'rotate-180 text-fuschia' : 'text-blue-500 '
                }`}
            />
        </h3>
    );
};

/**
 * LinkListBlock component
 * @description This component is responsible for rendering the list block in the footer links section
 * @param {*} { content = [] }
 * @return {*}
 */
export const LinkListBlock = (node) => {
    const {
        node: { content = [] }
    } = node;
    const ref = useRef(null);
    const { activeSection } = useContext(ActiveSectionContext);
    const [active, setActive] = useState(false);
    const listElement = content?.map(({ content }) => content);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        // Get the previous heading element
        const previusHeading = ref.current.previousElementSibling;
        // Set the active state based on the active section
        setActive(previusHeading?.id === activeSection);
    }, [activeSection]);

    return (
        <>
            <ul ref={ref} className={`md:block ${active ? 'block' : 'hidden'}`}>
                {listElement.map((block, i) => (
                    <LinkItem key={i} content={block} />
                ))}
            </ul>
            <hr className="my-4 border-blue-500 md:hidden" />
        </>
    );
};

/**
 * LinkItem component
 * @description This component is responsible for rendering the list item in the footer links section
 * @param {*} { content = [], active }
 * @return {*}
 */
export const LinkItem = (node) => {
    const { content } = node;
    const nodes = useMemo(() => content?.map(({ content }) => content).flat(), [content]);
    const { text, link } = useMemo(() => getTextLink(nodes[0]), [nodes]);

    return (
        <li className="mt-2 md:mt-3">
            <LinkNoPrefetch
                href={link}
                aria-label={decode(text)}
                className="text-xs text-white text-opacity-75 hover:text-opacity-100">
                {decode(text)}
            </LinkNoPrefetch>
        </li>
    );
};

export const FooterBlockNodes = {
    heading: LinkHeadingBlock,
    bulletList: LinkListBlock,
    orderedList: LinkListBlock
};
